/**
 * RouteMap — a stylised, hand-drawn SVG map of the Oradea / Bihor area with an
 * animated refrigerated truck that rides a dispatched route, pausing to "deliver"
 * at each stop. Fully offline: no map tiles, no external API — just inline SVG.
 *
 * Geography is charming, not accurate: town nodes sit in roughly their real
 * relative positions around the Oradea depot. The active route is a smooth
 * spline through the ordered stops; the truck follows it via `getPointAtLength`.
 *
 * Prop-based, controllable API (reused by the cinematic):
 *   <RouteMap
 *     stops={[{ id, eticheta, oras }]}   // ordered; depot (Oradea) is prepended
 *     autoPlay loop                       // uncontrolled convenience
 *     playing={bool}                      // OR drive play/pause from a parent
 *     onArrive={(i, stop) => …}           // truck reached stop i (0-based)
 *     onComplete={() => …}                // whole route delivered
 *     showControls                        // built-in play/pause/reset overlay
 *   />
 * Or grab a ref: const r = useRef<RouteMapHandle>(); r.current.play()/pause()/reset().
 */

import {
  type Easing,
  type MotionValue,
  animate,
  motion,
  useMotionValue,
  useTransform,
} from 'framer-motion'
import { Pause, Play, RotateCcw } from 'lucide-react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { TruckAnim } from '@/components/TruckAnim'
import { stripDiacritice } from '@/lib/parseOrder'
import { cn } from '@/lib/utils'

// --- geography -------------------------------------------------------------
export type Oras = 'oradea' | 'marghita' | 'salonta' | 'beius' | 'alesd' | 'stei'

interface OrasDef {
  nume: string
  x: number
  y: number
  /** label placement relative to the node */
  lx: number
  ly: number
  anchor: 'start' | 'middle' | 'end'
}

const VB_W = 760
const VB_H = 620

/** Town nodes — approximate relative geography around the Oradea depot. */
export const ORASE: Record<Oras, OrasDef> = {
  oradea: { nume: 'Oradea', x: 156, y: 188, lx: 0, ly: 30, anchor: 'middle' },
  marghita: { nume: 'Marghita', x: 452, y: 104, lx: 14, ly: 4, anchor: 'start' },
  alesd: { nume: 'Aleșd', x: 512, y: 246, lx: 15, ly: 4, anchor: 'start' },
  salonta: { nume: 'Salonta', x: 190, y: 470, lx: 0, ly: 30, anchor: 'middle' },
  beius: { nume: 'Beiuș', x: 452, y: 424, lx: 15, ly: 4, anchor: 'start' },
  stei: { nume: 'Ștei', x: 566, y: 542, lx: 0, ly: 30, anchor: 'middle' },
}

const DEPOT: Oras = 'oradea'

/** Map a client `localitate` string to a town node (diacritic-insensitive). */
export function orasDinLocalitate(loc: string): Oras {
  const n = stripDiacritice(loc.toLowerCase()).trim()
  if (n.startsWith('marghit')) return 'marghita'
  if (n.startsWith('salont')) return 'salonta'
  if (n.startsWith('beius')) return 'beius'
  if (n.startsWith('ales')) return 'alesd'
  if (n.startsWith('stei')) return 'stei'
  return 'oradea'
}

/** Straight-line distance between two towns (for nearest-neighbour ordering). */
export function distOras(a: Oras, b: Oras): number {
  const p = ORASE[a]
  const q = ORASE[b]
  return Math.hypot(p.x - q.x, p.y - q.y)
}

// --- static decor: roads + rivers ------------------------------------------
/** Curved road between two towns, bowed perpendicular by `bow` px. */
function roadPath(a: Oras, b: Oras, bow: number): string {
  const p = ORASE[a]
  const q = ORASE[b]
  const mx = (p.x + q.x) / 2
  const my = (p.y + q.y) / 2
  const dx = q.x - p.x
  const dy = q.y - p.y
  const len = Math.hypot(dx, dy) || 1
  const cx = mx + (-dy / len) * bow
  const cy = my + (dx / len) * bow
  return `M ${p.x} ${p.y} Q ${cx} ${cy} ${q.x} ${q.y}`
}

const ROADS: { a: Oras; b: Oras; bow: number }[] = [
  { a: 'oradea', b: 'marghita', bow: -34 },
  { a: 'oradea', b: 'alesd', bow: -20 },
  { a: 'oradea', b: 'salonta', bow: 22 },
  { a: 'oradea', b: 'beius', bow: 46 },
  { a: 'alesd', b: 'beius', bow: 28 },
  { a: 'salonta', b: 'beius', bow: -26 },
  { a: 'beius', b: 'stei', bow: -18 },
  { a: 'marghita', b: 'alesd', bow: 24 },
]

// hand-drawn county silhouette + two river squiggles (Crișul Repede / Negru)
const COUNTY =
  'M 118 96 C 250 54 470 48 626 96 C 700 132 706 250 686 356 C 668 470 588 566 452 574 C 300 582 156 566 106 452 C 62 350 66 168 118 96 Z'
const RIU_REPEDE = 'M 700 214 C 560 236 420 210 300 232 C 210 248 150 214 60 236'
const RIU_NEGRU = 'M 610 560 C 520 512 470 470 400 468 C 300 464 236 500 110 486'

// --- spline through waypoints ----------------------------------------------
interface Pt {
  x: number
  y: number
}

/** Catmull-Rom → cubic bézier: a smooth path passing through every point. */
function splinePath(pts: Pt[], tension = 0.9): string {
  const first = pts[0]
  if (!first) return ''
  if (pts.length === 1) return `M ${first.x} ${first.y}`
  let d = `M ${first.x} ${first.y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i] as Pt
    const p2 = pts[i + 1] as Pt
    const p0 = pts[i - 1] ?? p1
    const p3 = pts[i + 2] ?? p2
    const c1x = p1.x + ((p2.x - p0.x) / 6) * tension
    const c1y = p1.y + ((p2.y - p0.y) / 6) * tension
    const c2x = p2.x - ((p3.x - p1.x) / 6) * tension
    const c2y = p2.y - ((p3.y - p1.y) / 6) * tension
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${p2.x} ${p2.y}`
  }
  return d
}

/** Arc-length along a mounted path nearest to each waypoint (dense sampling). */
function measureWaypoints(path: SVGPathElement, pts: Pt[]): number[] {
  const total = path.getTotalLength()
  const SAMPLES = 480
  const samples: { x: number; y: number; l: number }[] = []
  for (let i = 0; i <= SAMPLES; i++) {
    const l = (total * i) / SAMPLES
    const q = path.getPointAtLength(l)
    samples.push({ x: q.x, y: q.y, l })
  }
  return pts.map((pt, idx) => {
    if (idx === 0) return 0
    if (idx === pts.length - 1) return total
    let best = 0
    let bd = Number.POSITIVE_INFINITY
    for (const s of samples) {
      const dx = s.x - pt.x
      const dy = s.y - pt.y
      const dd = dx * dx + dy * dy
      if (dd < bd) {
        bd = dd
        best = s.l
      }
    }
    return best
  })
}

const PAUSE_S = 0.75 // dwell at each stop

interface Keyframes {
  values: number[]
  times: number[]
  eases: Easing[]
  duration: number
}

/** One keyframed animation over arc-length, with a hold baked in at each stop. */
function buildKeyframes(wp: number[]): Keyframes | null {
  const legs = wp.length - 1
  if (legs < 1) return null
  const values: number[] = [wp[0] ?? 0]
  const durs: number[] = []
  const eases: Easing[] = []
  for (let i = 0; i < legs; i++) {
    const from = wp[i] ?? 0
    const to = wp[i + 1] ?? from
    const travel = Math.min(2.4, Math.max(0.8, (to - from) / 205))
    values.push(to)
    durs.push(travel)
    eases.push('easeInOut')
    values.push(to) // hold → dwell at the stop
    durs.push(PAUSE_S)
    eases.push('linear')
  }
  const duration = durs.reduce((a, b) => a + b, 0)
  let acc = 0
  const times = [0]
  for (const d of durs) {
    acc += d
    times.push(acc / duration)
  }
  return { values, times, eases, duration }
}

// --- stop points (fan overlapping stops around a shared town) --------------
function stopPoints(stops: RouteStop[]): Pt[] {
  const seen: Record<string, number> = {}
  return stops.map((s) => {
    const base = ORASE[s.oras]
    const k = seen[s.oras] ?? 0
    seen[s.oras] = k + 1
    // single stop per town sits on the node; depot-town stops always offset
    const needsOffset = s.oras === DEPOT || k > 0
    if (!needsOffset) return { x: base.x, y: base.y }
    const ang = -Math.PI / 2 + k * (Math.PI / 2.5) + (s.oras === DEPOT ? -0.5 : 0)
    const r = 22
    return { x: base.x + Math.cos(ang) * r, y: base.y + Math.sin(ang) * r }
  })
}

// --- public API ------------------------------------------------------------
export interface RouteStop {
  /** stable id (comandă or client id) */
  id: string
  /** label shown on the stop pin tooltip / list */
  eticheta: string
  oras: Oras
}

export interface RouteMapHandle {
  play: () => void
  pause: () => void
  reset: () => void
  toggle: () => void
}

export interface RouteMapProps {
  stops: RouteStop[]
  /** controlled play state; when provided, built-in controls are hidden */
  playing?: boolean
  autoPlay?: boolean
  loop?: boolean
  showControls?: boolean
  onArrive?: (index: number, stop: RouteStop) => void
  onComplete?: () => void
  className?: string
}

export const RouteMap = forwardRef<RouteMapHandle, RouteMapProps>(function RouteMap(
  {
    stops,
    playing,
    autoPlay = false,
    loop = false,
    showControls = true,
    onArrive,
    onComplete,
    className,
  },
  ref,
) {
  const pathRef = useRef<SVGPathElement | null>(null)
  const dist = useMotionValue(0)

  const controlsRef = useRef<ReturnType<typeof animate> | null>(null)
  const wpDistRef = useRef<number[]>([])
  const lastWpRef = useRef(0)
  const finishedRef = useRef(false)
  const onArriveRef = useRef(onArrive)
  const onCompleteRef = useRef(onComplete)
  const loopRef = useRef(loop)

  const [revision, setRevision] = useState(0)
  const [playingState, setPlayingState] = useState(false)
  const [delivered, setDelivered] = useState(0)
  const [arriving, setArriving] = useState<number | null>(null)

  onArriveRef.current = onArrive
  onCompleteRef.current = onComplete
  loopRef.current = loop

  const controlled = playing !== undefined
  const points = useMemo(() => stopPoints(stops), [stops])
  const waypoints = useMemo<Pt[]>(
    () => [{ x: ORASE[DEPOT].x, y: ORASE[DEPOT].y }, ...points],
    [points],
  )
  const routeD = useMemo(() => splinePath(waypoints), [waypoints])
  const routeSig = useMemo(() => stops.map((s) => `${s.id}:${s.oras}`).join('|'), [stops])

  // progress for the readout bar (0..100)
  const pct = useTransform(dist, (v) => {
    const t = wpDistRef.current[wpDistRef.current.length - 1] ?? 0
    return t > 0 ? Math.min(100, (v / t) * 100) : 0
  })

  const stopAnim = useCallback(() => {
    controlsRef.current?.stop()
    controlsRef.current = null
  }, [])

  const startFresh = useCallback(() => {
    const wp = wpDistRef.current
    const kf = buildKeyframes(wp)
    if (!kf) return
    stopAnim()
    finishedRef.current = false
    dist.set(0)
    lastWpRef.current = 0
    setDelivered(0)
    setArriving(null)
    controlsRef.current = animate(dist, kf.values, {
      duration: kf.duration,
      times: kf.times,
      ease: kf.eases,
      onComplete: () => {
        finishedRef.current = true
        setPlayingState(false)
        onCompleteRef.current?.()
        if (loopRef.current) startFresh()
      },
    })
    setPlayingState(true)
  }, [dist, stopAnim])

  const play = useCallback(() => {
    if (wpDistRef.current.length < 2) return
    if (controlsRef.current && !finishedRef.current) {
      controlsRef.current.play()
      setPlayingState(true)
      return
    }
    startFresh()
  }, [startFresh])

  const pause = useCallback(() => {
    controlsRef.current?.pause()
    setPlayingState(false)
  }, [])

  const reset = useCallback(() => {
    stopAnim()
    finishedRef.current = false
    dist.set(0)
    lastWpRef.current = 0
    setDelivered(0)
    setArriving(null)
    setPlayingState(false)
  }, [dist, stopAnim])

  const toggle = useCallback(() => {
    if (playingState) pause()
    else play()
  }, [playingState, pause, play])

  useImperativeHandle(ref, () => ({ play, pause, reset, toggle }), [play, pause, reset, toggle])

  // arrival detection: which waypoint have we reached?
  useEffect(() => {
    const unsub = dist.on('change', (v) => {
      const wp = wpDistRef.current
      if (wp.length < 2) return
      let idx = 0
      for (let i = 1; i < wp.length; i++) {
        if (v >= (wp[i] ?? Number.POSITIVE_INFINITY) - 1) idx = i
      }
      if (idx !== lastWpRef.current) {
        lastWpRef.current = idx
        const stop = stops[idx - 1]
        if (idx >= 1 && stop) {
          setDelivered(idx)
          setArriving(idx - 1)
          onArriveRef.current?.(idx - 1, stop)
        }
      }
    })
    return unsub
  }, [dist, stops])

  // measure waypoint arc-lengths whenever the route geometry changes
  useLayoutEffect(() => {
    reset()
    setRevision((r) => r + 1)
    const path = pathRef.current
    if (!path || waypoints.length < 2) {
      wpDistRef.current = []
      return
    }
    wpDistRef.current = measureWaypoints(path, waypoints)
    if (autoPlay || (controlled && playing)) {
      // let the redraw settle a beat before rolling
      const t = setTimeout(() => startFresh(), 260)
      return () => clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSig])

  // controlled play/pause
  useEffect(() => {
    if (!controlled) return
    if (playing) play()
    else pause()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, controlled])

  const total = stops.length
  const showBuiltInControls = showControls && !controlled

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border border-border bg-card shadow-paper',
        className,
      )}
    >
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="block h-auto w-full"
        role="img"
        aria-label="Hartă ilustrativă a traseului de livrare în județul Bihor"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="rm-canvas" cx="30%" cy="20%" r="90%">
            <stop offset="0%" stopColor="oklch(0.985 0.02 86)" />
            <stop offset="100%" stopColor="oklch(0.965 0.016 84)" />
          </radialGradient>
          <filter id="rm-soft" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow
              dx="0"
              dy="1.5"
              stdDeviation="2.5"
              floodColor="oklch(0.4 0.06 60)"
              floodOpacity="0.18"
            />
          </filter>
          <filter id="rm-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>

        <rect x={0} y={0} width={VB_W} height={VB_H} fill="url(#rm-canvas)" />

        {/* county silhouette */}
        <path
          d={COUNTY}
          fill="oklch(0.955 0.018 88)"
          stroke="var(--hairline)"
          strokeWidth={1.5}
          strokeDasharray="1 0"
        />
        {/* western frontier (Hungary) — dashed edge */}
        <path
          d="M 128 132 C 96 250 100 372 138 470"
          fill="none"
          stroke="var(--taupe)"
          strokeWidth={1.2}
          strokeDasharray="2 7"
          opacity={0.5}
          strokeLinecap="round"
        />
        <text x={112} y={120} fontSize={11} fill="var(--taupe)" opacity={0.7} fontStyle="italic">
          frontieră
        </text>

        {/* rivers */}
        <g
          fill="none"
          stroke="oklch(0.72 0.05 232)"
          strokeWidth={2}
          opacity={0.32}
          strokeLinecap="round"
        >
          <path d={RIU_REPEDE} />
          <path d={RIU_NEGRU} />
        </g>

        {/* road network */}
        <g fill="none" strokeLinecap="round">
          {ROADS.map((r) => (
            <path
              key={`${r.a}-${r.b}`}
              d={roadPath(r.a, r.b, r.bow)}
              stroke="var(--hairline)"
              strokeWidth={6}
            />
          ))}
          {ROADS.map((r) => (
            <path
              key={`c-${r.a}-${r.b}`}
              d={roadPath(r.a, r.b, r.bow)}
              stroke="var(--card)"
              strokeWidth={2.4}
              strokeDasharray="1 8"
              opacity={0.9}
            />
          ))}
        </g>

        {/* active route */}
        {routeD && waypoints.length > 1 && (
          <g key={routeSig}>
            <path
              d={routeD}
              fill="none"
              stroke="var(--amber)"
              strokeWidth={9}
              strokeLinecap="round"
              opacity={0.22}
              filter="url(#rm-glow)"
            />
            <motion.path
              ref={pathRef}
              d={routeD}
              fill="none"
              stroke="var(--amber-strong)"
              strokeWidth={3.4}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.9, ease: 'easeInOut' }}
            />
          </g>
        )}
        {/* keep a measurable path in the DOM even before a route is drawn */}
        {(!routeD || waypoints.length <= 1) && (
          <path ref={pathRef} d="M 0 0" fill="none" stroke="none" />
        )}

        {/* town nodes */}
        {(Object.keys(ORASE) as Oras[]).map((key) => {
          const o = ORASE[key]
          if (key === DEPOT) return null
          return (
            <g key={key}>
              <circle
                cx={o.x}
                cy={o.y}
                r={6.5}
                fill="var(--card)"
                stroke="var(--taupe)"
                strokeWidth={2}
              />
              <circle cx={o.x} cy={o.y} r={2.4} fill="var(--taupe)" />
              <text
                x={o.x + o.lx}
                y={o.y + o.ly}
                fontSize={13}
                fontWeight={500}
                fill="var(--ink-soft)"
                textAnchor={o.anchor}
                className="font-sans"
              >
                {o.nume}
              </text>
            </g>
          )
        })}

        {/* stop pins */}
        {points.map((p, i) => {
          const stop = stops[i]
          if (!stop) return null
          const done = i < delivered
          const isArriving = arriving === i
          return (
            <g key={stop.id}>
              {isArriving && (
                <motion.circle
                  cx={p.x}
                  cy={p.y}
                  r={10}
                  fill="none"
                  stroke="var(--pine)"
                  strokeWidth={2}
                  initial={{ scale: 0.6, opacity: 0.7 }}
                  animate={{ scale: 2.1, opacity: 0 }}
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: 'easeOut' }}
                />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r={11}
                fill={done ? 'var(--pine)' : 'var(--amber-strong)'}
                stroke="var(--card)"
                strokeWidth={2.5}
                filter="url(#rm-soft)"
              />
              {done ? (
                <path
                  d={`M ${p.x - 4.2} ${p.y} l 2.8 3 l 5.2 -6`}
                  fill="none"
                  stroke="var(--pine-foreground)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <text
                  x={p.x}
                  y={p.y + 3.6}
                  fontSize={11}
                  fontWeight={700}
                  fill="var(--amber-ink)"
                  textAnchor="middle"
                  className="font-mono"
                >
                  {i + 1}
                </text>
              )}
            </g>
          )
        })}

        {/* depot marker (Oradea) */}
        <g filter="url(#rm-soft)">
          <circle
            cx={ORASE.oradea.x}
            cy={ORASE.oradea.y}
            r={13}
            fill="var(--amber)"
            stroke="var(--card)"
            strokeWidth={2.5}
          />
          <g
            transform={`translate(${ORASE.oradea.x - 6.5} ${ORASE.oradea.y - 6})`}
            fill="none"
            stroke="var(--amber-ink)"
            strokeWidth={1.6}
            strokeLinejoin="round"
          >
            <path d="M 0 5 L 6.5 0 L 13 5 L 13 12 L 0 12 Z" fill="var(--amber-ink)" stroke="none" />
            <rect x={4.5} y={7.5} width={4} height={4.5} fill="var(--amber)" stroke="none" />
          </g>
        </g>
        <text
          x={ORASE.oradea.x + ORASE.oradea.lx}
          y={ORASE.oradea.y + ORASE.oradea.ly}
          fontSize={13}
          fontWeight={600}
          fill="var(--ink-soft)"
          textAnchor="middle"
          className="font-sans"
        >
          Oradea
        </text>
        <text
          x={ORASE.oradea.x}
          y={ORASE.oradea.y + ORASE.oradea.ly + 15}
          fontSize={10.5}
          fill="var(--taupe)"
          textAnchor="middle"
          className="font-sans"
        >
          Depozit Exemplu SRL
        </text>

        {/* the truck */}
        {waypoints.length > 1 && (
          <TruckAnim pathRef={pathRef} dist={dist} size={1} revision={revision} />
        )}

        {/* compass */}
        <g transform="translate(700 92)" opacity={0.8}>
          <circle r={17} fill="var(--card)" stroke="var(--hairline)" strokeWidth={1.2} />
          <path d="M 0 -11 L 4 3 L 0 0 L -4 3 Z" fill="var(--amber-strong)" />
          <text
            x={0}
            y={-13.5}
            fontSize={9}
            fontWeight={700}
            fill="var(--ink-soft)"
            textAnchor="middle"
            className="font-sans"
          >
            N
          </text>
        </g>
      </svg>

      {/* readout */}
      <div className="pointer-events-none absolute left-3 top-3 flex flex-col gap-1.5">
        <div className="pointer-events-auto inline-flex items-center gap-2 rounded-lg border border-border bg-card/90 px-3 py-1.5 text-xs shadow-paper backdrop-blur-sm">
          <span className="font-medium text-muted-foreground">Traseu</span>
          <span className="nums font-mono font-semibold text-amber-ink">
            {delivered}/{total}
          </span>
          <span className="text-muted-foreground">opriri livrate</span>
        </div>
        {total > 0 && (
          <div className="pointer-events-auto h-1.5 w-40 overflow-hidden rounded-full bg-muted">
            <ProgressFill pct={pct} />
          </div>
        )}
      </div>

      {/* built-in controls */}
      {showBuiltInControls && total > 0 && (
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
          <button
            type="button"
            onClick={toggle}
            aria-label={playingState ? 'Pauză' : 'Pornește traseul'}
            className="inline-flex size-9 items-center justify-center rounded-full border border-border bg-primary text-primary-foreground shadow-paper transition-all hover:bg-amber-strong"
          >
            {playingState ? <Pause className="size-4" /> : <Play className="size-4" />}
          </button>
          <button
            type="button"
            onClick={reset}
            aria-label="Reia de la început"
            className="inline-flex size-9 items-center justify-center rounded-full border border-border bg-card text-ink-soft shadow-paper transition-all hover:bg-accent"
          >
            <RotateCcw className="size-4" />
          </button>
        </div>
      )}
    </div>
  )
})

function ProgressFill({ pct }: { pct: MotionValue<number> }) {
  const width = useTransform(pct, (v) => `${v}%`)
  return <motion.div className="h-full rounded-full bg-amber" style={{ width }} />
}
